import React, { useContext } from 'react';
import { css } from '@emotion/react';
import { ThemeContext } from './theme-provider';
import Accordion from './accordion';
import PortableText from './portable-text';
import PDF from './pdf';
import HistoryGallery from './history-gallery';
import { getAboutSections, getHistoryImages } from '../about-sections';
import { trimTrailingBlankBlocks } from '../content';

function People({ intro, people = [] }) {
  const theme = useContext(ThemeContext);

  return (
    <>
      {intro && (
        <div
          css={css`
            margin-bottom: var(--margin);
          `}
        >
          <PortableText blocks={intro} />
        </div>
      )}
      {people.map((person, index) => (
        <div key={person._key || index}>
          {person._rawContent ? (
            <button
              type="button"
              css={css`
                border: 0;
                padding: 0;
                color: inherit;
                background: none;
                font: inherit;
                text-align: left;
                text-decoration: underline;
                cursor: pointer;
                transition: color 0.6s var(--curve);
                &:hover {
                  color: var(--primary);
                  transition: color 0s;
                }
                &:focus-visible {
                  outline: 2px solid var(--black);
                  outline-offset: 4px;
                }
              `}
              onClick={() => {
                theme.setModalContent(person._rawContent);
                theme.setModalTitle(person.name);
                theme.setModalImage(person.image);
                theme.setShowModal(true);
              }}
            >
              <span
                css={css`
                  text-transform: uppercase;
                `}
              >
                {person.name}
              </span>
              {person.position ? `, ${person.position}` : ''}
            </button>
          ) : (
            <>
              <span
                css={css`
                  text-transform: uppercase;
                `}
              >
                {person.name}
              </span>
              {person.position ? `, ${person.position}` : ''}
            </>
          )}
        </div>
      ))}
    </>
  );
}

export default function AboutSections({ about }) {
  return (
    <div
      css={css`
        margin-bottom: var(--bigMargin);
      `}
    >
      {getAboutSections(about).map((section, index) => (
        <Accordion
          key={section._key || index}
          id={`about-section-${section._key || index}`}
          title={section.title}
        >
          {section.kind === 'team' && (
            <People intro={about._rawTeamIntro} people={about.staff} />
          )}
          {section.kind === 'advisors' && (
            <People intro={about._rawAdvisorsIntro} people={about.curators} />
          )}
          {section.kind === 'board' && (
            <People
              intro={about._rawManagementIntro}
              people={about.managementCommittee}
            />
          )}
          {section.kind === 'referenceGroup' && (
            <People
              intro={about._rawReferenceGroupIntro}
              people={about.referenceGroup}
            />
          )}
          {section.kind === 'content' && (
            <PortableText blocks={section._rawContent || section.content} />
          )}
          {section.kind === 'annualReports' && (
            <div
              css={css`
                padding-left: var(--gutter);
              `}
            >
              {(about.annualReports || []).map((pdf, index) => (
                <PDF
                  key={pdf._key || index}
                  link={pdf.file?.asset.originalFilename
                    ? `${pdf.file.asset.url}/${encodeURIComponent(pdf.file.asset.originalFilename)}`
                    : pdf.file?.asset.url}
                  text={pdf.title}
                />
              ))}
            </div>
          )}
          {section.kind === 'history' && (
            <>
              <PortableText blocks={trimTrailingBlankBlocks(about._rawBuildingText)} />
              <HistoryGallery
                images={getHistoryImages(about)}
                introduction={about._rawBuildingText}
              />
            </>
          )}
        </Accordion>
      ))}
    </div>
  );
}
